import * as React from 'react';
import { useMemo, useState } from 'react';
import { Clear as ClearIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
    DateRange as DateRangePickerRange,
    DateRangePicker,
} from 'materialui-daterange-picker';
import {
    AppBar,
    Box,
    Fade,
    IconButton,
    Paper,
    TextField,
    TextFieldProps,
    Toolbar,
    Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DATE_FORMAT } from '../../../../helpers';

export type DateRange = [Dayjs, Dayjs];

interface DDDateRangePickerProps {
    error?: boolean;
    helperText?: string;
    disableGutters?: boolean;
    disabled?: boolean;
    label?: string;
    onChange: (date: DateRange) => void;
    onClear?: () => void;
    value?: DateRange;
    variant?: TextFieldProps['variant'];
}

export function DDateRangePicker(props: DDDateRangePickerProps) {
    const {
        disabled = false,
        label,
        error,
        disableGutters = false,
        helperText,
        onChange,
        onClear,
        value,
    } = props;

    const hasValue = !!(value && value.length > 1 && value[0] && value[1]);

    const [open, setOpen] = useState(false);

    const localValue: DateRangePickerRange = useMemo(() => {
        return {
            startDate: hasValue ? value![0].toDate() : undefined,
            endDate: hasValue ? value![1].toDate() : undefined,
        };
    }, [hasValue, value]);

    return (
        <Box>
            <TextField
                autoComplete="off"
                disabled={disabled}
                style={{
                    marginBottom: !disableGutters ? 20 : 0,
                    marginTop: 0,
                    width: '100%',
                }}
                label={label}
                value={
                    hasValue
                        ? `${value[0].format(DATE_FORMAT)} - ${value[1].format(DATE_FORMAT)}`
                        : ''
                }
                error={Boolean(error)}
                helperText={helperText || ''}
                margin={'dense'}
                size={'small'}
                onChange={() => {
                    //
                }}
                InputProps={{
                    endAdornment: (
                        <React.Fragment>
                            {hasValue && onClear ? (
                                <IconButton
                                    size={'small'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClear();
                                    }}
                                >
                                    <ClearIcon fontSize={'small'} />
                                </IconButton>
                            ) : null}
                            <ArrowDropDownIcon
                                onClick={() => {
                                    setOpen(true);
                                }}
                            />
                        </React.Fragment>
                    ),
                }}
                onClick={() => {
                    setOpen(true);
                }}
            />
            <DDDateRangePickerOverlay
                onChange={(dateRange) => {
                    onChange([
                        dayjs(dateRange.startDate),
                        dayjs(dateRange.endDate),
                    ]);
                }}
                open={open}
                setOpen={setOpen}
                value={localValue}
            />
        </Box>
    );
}

interface DDDateRangePickerOverlayProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onChange: (date: DateRangePickerRange) => void;
    value?: DateRangePickerRange;
}

function DDDateRangePickerOverlay(props: DDDateRangePickerOverlayProps) {
    const { onChange, value: initialValue, setOpen, open } = props;

    return (
        <Fade in={open}>
            <Box
                sx={{
                    display: !open ? 'none' : undefined,
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    zIndex: 1200,
                }}
                onClick={() => {
                    setOpen(false);
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'black',
                        zIndex: 1250,
                        opacity: 0.2,
                    }}
                />
                <Box
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2000,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <Paper>
                        <AppBar variant={'outlined'}>
                            <Toolbar
                                disableGutters
                                variant={'dense'}
                                sx={{ px: 2 }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        mr: 6,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant={'h5'}>
                                        Rango de fechas
                                    </Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1 }} />
                                <IconButton
                                    color="inherit"
                                    size={'small'}
                                    onClick={() => {
                                        setOpen(false);
                                    }}
                                >
                                    <ClearIcon />
                                </IconButton>
                            </Toolbar>
                        </AppBar>
                        <Box sx={{ my: 2, mb: 6 }} />
                        <DateRangePicker
                            open={open}
                            initialDateRange={initialValue}
                            onChange={(val) => {
                                if (onChange) {
                                    onChange(val);
                                    setOpen(false);
                                }
                            }}
                            toggle={() => {}}
                            closeOnClickOutside={true}
                        />
                    </Paper>
                </Box>
            </Box>
        </Fade>
    );
}
